import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import AnimeCard from "@/components/base/AnimeCard";
import { animeList } from "@/mocks/anime";
import { categories } from "@/mocks/categories";
import { organizationSchema, breadcrumbSchema } from "@/lib/seoData";

interface FilterTab {
  key: string;
  label: string;
}

const allTabs: FilterTab[] = [
  { key: "all", label: "全部" },
  ...categories.map((c) => ({ key: c.slug, label: c.name })),
];

function matchAnime(anime: (typeof animeList)[number], key: string): boolean {
  switch (key) {
    case "all":
      return true;
    case "guoman":
      return anime.region === "国漫";
    case "riman":
      return anime.region === "日漫";
    case "xinfan":
      return anime.year === "2026";
    case "rexue":
      return anime.tags.includes("热血");
    default:
      return anime.category === key;
  }
}

export default function Categories() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeKey = searchParams.get("cat") || "all";

  const activeTab = allTabs.find((t) => t.key === activeKey) ?? allTabs[0];

  const list = useMemo(
    () => animeList.filter((a) => matchAnime(a, activeKey)),
    [activeKey],
  );

  const handleSwitch = (key: string) => {
    setSearchParams(key === "all" ? {} : { cat: key });
  };

  const jsonLd = [
    organizationSchema,
    breadcrumbSchema([
      { name: "首页", path: "/" },
      { name: "全部动漫", path: "/categories" },
    ]),
  ];

  return (
    <Layout>
      <Seo
        title={`${activeTab.label}动漫列表 - 动漫网全部动漫 / 国漫日漫检索入口`}
        description={`动漫网${activeTab.label}动漫分类大全，汇集${activeTab.label}动画作品列表，支持国漫日漫检索、新番索引与动画库，找漫画、追更新一站搞定。`}
        keywords={`动漫网,动漫网全部动漫列表,${activeTab.label}动漫,动漫网漫画分类大全,动漫网国漫专区,动漫网日漫检索`}
        canonical={`/categories${activeKey === "all" ? "" : `?cat=${activeKey}`}`}
        jsonLd={jsonLd}
      />

      <section className="bg-background-100 pt-24 pb-14 md:pb-20">
        <div className="mx-auto px-4 md:px-6">
          <Breadcrumb items={[{ label: "全部动漫", path: "/categories" }]} />
          <div className="mt-4">
            <span className="font-heading text-sm text-primary-600">动漫网全部动漫列表</span>
            <h1 className="mt-2 font-heading text-2xl text-foreground-950 md:text-4xl">
              {activeTab.label}动漫 · {list.length} 部
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-foreground-600 md:text-base">
              国漫专区、日漫检索、新番索引与动画库一站配齐，点选分类即可切换浏览对应动漫列表。
            </p>
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-10">
        <div className="mx-auto px-4 md:px-6">
          <div className="flex flex-wrap items-center gap-2 rounded-full border border-background-200/70 bg-background-100 p-1 w-fit">
            {allTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => handleSwitch(tab.key)}
                className={`whitespace-nowrap rounded-full px-5 py-2 text-sm font-medium transition-colors ${
                  activeKey === tab.key
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-700 hover:bg-background-50 hover:text-foreground-950"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {list.map((anime) => (
              <AnimeCard key={anime.id} anime={anime} />
            ))}
          </div>

          {list.length === 0 && (
            <div className="mt-10 rounded-lg border border-dashed border-background-300 bg-background-100 p-10 text-center text-sm text-foreground-600">
              该分类下暂时没有收录的动漫，欢迎浏览其他分类。
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}